import FJUtil from './FJUtil.js';

function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
}

function floatTo16BitPCM(output, offset, input) {
    for (var i = 0; i < input.length; i++, offset += 2) {
        var s = Math.max(-1, Math.min(1, input[i]));
        output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
}

const FJAudioUtil = {
    encodeWAV: function (samples, channels = 2) {
        let sampleRate = FJUtil.getAudioSampleRate();
        let numChannels = channels;
        let bitDepth = 16;
        var bytesPerSample = bitDepth / 8;
        var blockAlign = numChannels * bytesPerSample;
        var buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
        var view = new DataView(buffer);

        /* RIFF identifier */
        writeString(view, 0, 'RIFF');
        /* RIFF chunk length */
        view.setUint32(4, 36 + samples.length * bytesPerSample, true);
        /* RIFF type */
        writeString(view, 8, 'WAVE');
        /* format chunk identifier */
        writeString(view, 12, 'fmt ');
        /* format chunk length */
        view.setUint32(16, 16, true);
        /* sample format (raw) */
        view.setUint16(20, 1, true);
        /* channel count */
        view.setUint16(22, numChannels, true);
        /* sample rate */
        view.setUint32(24, sampleRate, true);
        /* byte rate (sample rate * block align) */
        view.setUint32(28, sampleRate * blockAlign, true);
        /* block align (channel count * bytes per sample) */
        view.setUint16(32, blockAlign, true);
        /* bits per sample */
        view.setUint16(34, bitDepth, true);
        /* data chunk identifier */
        writeString(view, 36, 'data');
        /* data chunk length */
        view.setUint32(40, samples.length * bytesPerSample, true);

        floatTo16BitPCM(view, 44, samples);

        return buffer;
    },

    extractAudioData: function (audioData, startSample, endSample) {
        // 确保索引在有效范围内
        const start = Math.max(0, startSample);
        const end = Math.min(audioData.length, endSample);

        // 创建新的 Float32Array 来存储截取的数据
        const length = end - start;
        const extractedData = new Float32Array(length);

        // 复制指定范围的数据
        extractedData.set(audioData.subarray(start, end));

        return extractedData;
    },

    /**
     * @desc 时间转换为样本
     * @param {number} timeInSeconds 时间
     * @param {number} sampleRate 采样率
     * @returns {number} 样本
     */
    timeToSamples: function (timeInSeconds, sampleRate) {
        return Math.floor(timeInSeconds * sampleRate);
    },

    /**
     * @desc 提取多段音频数据并合并
     * @param {Float32Array} audioData 原始音频数据
     * @param {Array<{start: number, end: number}>} segments 多段取样区间
     * @param {number} channels 通道数
     * @returns {Float32Array} 合并后的音频数据
     */
    extractMultiSegments: function (audioData, segments, channels = 2) {
        // 计算总共需要的长度
        const totalLength = segments.reduce((acc, segment) => {
            const segmentLength = (segment.end - segment.start) * channels;
            return acc + segmentLength;
        }, 0);

        // 创建新的数组来存储合并后的数据
        const mergedData = new Float32Array(totalLength);

        let offset = 0;
        segments.forEach(segment => {
            // 使用现有的 extractMultiChannelAudio 来提取每一段
            const segmentData = this.extractMultiChannelAudio(audioData, channels, segment.start, segment.end);

            // 将提取的片段复制到合并数组中
            mergedData.set(segmentData, offset);
            offset += segmentData.length;
        });

        return mergedData;
    },

    /**
     * @desc 提取多通道音频数据
     * @param {Float32Array} audioData 原始音频数据
     * @param {number} channels 通道数
     * @param {number} startSample 开始样本
     * @param {number} endSample 结束样本
     * @returns {Float32Array} 提取后的音频数据
     */
    extractMultiChannelAudio: function (audioData, channels, startSample, endSample) {
        const samplesPerChannel = audioData.length / channels;
        const start = Math.max(0, startSample) * channels;
        const end = Math.min(samplesPerChannel, endSample) * channels;

        return audioData.subarray(start, end);
    },

    getMediaDuration: url => {
        return new Promise((resolve, reject) => {
            // 根据Blob的MIME类型创建对应的媒体元素
            const mediaElement = document.createElement('audio');

            mediaElement.addEventListener('seeked', () => {
                resolve(mediaElement.duration);
            });

            // 监听元数据加载完成事件
            mediaElement.addEventListener('loadedmetadata', () => {
                if (mediaElement.duration === Infinity) {
                    mediaElement.currentTime = 10000;
                } else {
                    // 获取时长并解析Promise
                    resolve(mediaElement.duration);
                }
            });

            // 监听错误事件
            mediaElement.addEventListener('error', reject);

            // 设置媒体元素的src为Blob的URL
            mediaElement.src = url;
        });
    },
};

export default FJAudioUtil;
